/**
 * Helpers for progressive <video> load / buffer status.
 * Championship clips are often large progressive MP4s with the index at the end,
 * so duration may be unknown until nearly the whole file has arrived.
 */

export function bufferedEndSeconds(video) {
  if (!video?.buffered?.length) return 0;
  try {
    return Number(video.buffered.end(video.buffered.length - 1)) || 0;
  } catch {
    return 0;
  }
}

export function bufferedAheadSeconds(video, atSec = null) {
  if (!video?.buffered?.length) return 0;
  const t =
    atSec != null && Number.isFinite(Number(atSec))
      ? Number(atSec)
      : Number(video.currentTime) || 0;
  try {
    for (let i = 0; i < video.buffered.length; i += 1) {
      const start = video.buffered.start(i);
      const end = video.buffered.end(i);
      if (t >= start && t <= end) return Math.max(0, end - t);
    }
  } catch {
    return 0;
  }
  return 0;
}

/**
 * @param {{
 *   samples: Array<{ atMs: number, bufferedEnd: number }>,
 *   nowMs?: number,
 *   windowMs?: number,
 * }} args
 * @returns {number|null} buffered-seconds growth per second, or null
 */
export function bufferedDownloadRate({
  samples,
  nowMs = Date.now(),
  windowMs = 8000,
} = {}) {
  const rows = (samples || []).filter(
    (s) => s && Number.isFinite(s.atMs) && Number.isFinite(s.bufferedEnd)
  );
  if (rows.length < 2) return null;
  const cutoff = nowMs - Math.max(1000, windowMs);
  const recent = rows.filter((s) => s.atMs >= cutoff);
  const use = recent.length >= 2 ? recent : rows.slice(-6);
  if (use.length < 2) return null;
  const first = use[0];
  const last = use[use.length - 1];
  const dt = (last.atMs - first.atMs) / 1000;
  const db = last.bufferedEnd - first.bufferedEnd;
  if (!(dt > 0.2) || !(db >= 0)) return null;
  return db / dt;
}

/**
 * Best-effort load progress for UI.
 * @returns {{
 *   percent: number|null,
 *   etaSec: number|null,
 *   bufferedEnd: number,
 *   duration: number,
 *   aheadSec: number,
 *   indeterminate: boolean,
 * }}
 */
export function estimateVideoLoadProgress(video, {
  samples = [],
  nowMs = Date.now(),
} = {}) {
  const bufferedEnd = bufferedEndSeconds(video);
  const duration = Number(video?.duration);
  const hasDuration = Number.isFinite(duration) && duration > 0.5;
  const aheadSec = bufferedAheadSeconds(video);
  const rate = bufferedDownloadRate({ samples, nowMs });

  if (hasDuration) {
    const percent = Math.max(
      0,
      Math.min(100, Math.round((bufferedEnd / duration) * 100))
    );
    const remaining = Math.max(0, duration - bufferedEnd);
    const etaSec =
      rate != null && rate > 0.02
        ? Math.max(0, Math.ceil(remaining / rate))
        : null;
    return {
      percent,
      etaSec,
      bufferedEnd,
      duration,
      aheadSec,
      indeterminate: percent < 1 && bufferedEnd < 0.05,
    };
  }

  // No duration yet (moov often at end of large championship MP4s).
  return {
    percent: null,
    etaSec: null,
    bufferedEnd,
    duration: 0,
    aheadSec,
    indeterminate: true,
  };
}

export function formatEtaSeconds(etaSec) {
  if (etaSec == null || !Number.isFinite(etaSec)) return null;
  const s = Math.max(0, Math.ceil(etaSec));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r ? `${m}m ${r}s` : `${m}m`;
}
